/**
 * API Error normalization for consistent error handling
 */

import { HTTPError } from 'ky';
import { z } from 'zod';

export type ErrorType = 'network' | 'timeout' | 'http' | 'validation' | 'auth' | 'unknown';

export class ApiError extends Error {
  readonly type: ErrorType;
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;
  readonly isRetryable: boolean;
  readonly cause?: Error;

  constructor(
    message: string,
    type: ErrorType = 'unknown',
    status: number = 0,
    options?: {
      code?: string;
      details?: unknown;
      isRetryable?: boolean;
      cause?: Error;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.code = options?.code;
    this.details = options?.details;
    this.isRetryable = options?.isRetryable ?? false;
    this.cause = options?.cause;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * Create ApiError from Ky HTTP error
   */
  static async fromKyError(error: unknown): Promise<ApiError> {
    if (error instanceof HTTPError) {
      const status = error.response.status;
      const type = this.getErrorType(status);
      const isRetryable = status >= 500 || status === 429;

      let message = error.message;
      let code: string | undefined;
      let details: unknown;

      try {
        // Try to parse JSON response body
        const body = await error.response.json();
        if (typeof body === 'object' && body !== null) {
          message = (body as any).message || (body as any).error || message;
          code = (body as any).code || (body as any).error_code;
          details = body;
        }
      } catch {
        // Response wasn't JSON or couldn't be parsed
        try {
          const text = await error.response.text();
          if (text) {
            details = text;
          }
        } catch {
          // Couldn't read response body at all
        }
      }

      return new ApiError(message, type, status, {
        code,
        details,
        isRetryable,
        cause: error,
      });
    }

    if (error instanceof Error) {
      // Network errors, timeouts, etc.
      const isTimeout = error.name === 'TimeoutError' || error.message.includes('timeout');
      const type: ErrorType = isTimeout ? 'timeout' : 'network';

      return new ApiError(error.message, type, 0, {
        isRetryable: true,
        cause: error,
      });
    }

    // Unknown error type
    return new ApiError('An unexpected error occurred', 'unknown', 0, {
      details: error,
      isRetryable: false,
    });
  }

  /**
   * Create ApiError from Zod validation error
   */
  static fromZodError(error: z.ZodError): ApiError {
    const issues = error.issues.map((issue) => ({
      path: issue.path.join('.'),
      message: issue.message,
      code: issue.code,
    }));

    const message =
      issues.length === 1 ? issues[0].message : `Validation failed: ${issues.length} errors`;

    return new ApiError(message, 'validation', 422, {
      code: 'VALIDATION_ERROR',
      details: { issues },
      isRetryable: false,
      cause: error,
    });
  }

  /**
   * Determine error type from HTTP status code
   */
  private static getErrorType(status: number): ErrorType {
    if (status === 401 || status === 403) return 'auth';
    if (status === 422) return 'validation';
    if (status >= 500) return 'http';
    if (status >= 400) return 'validation';
    return 'unknown';
  }

  /**
   * Check if error is of a specific type
   */
  is(type: ErrorType): boolean {
    return this.type === type;
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    switch (this.type) {
      case 'network':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'timeout':
        return 'The request took too long. Please try again.';
      case 'auth':
        if (this.status === 401) {
          return 'Your session has expired. Please log in again.';
        }
        return 'You do not have permission to perform this action.';
      case 'validation':
        if (this.details && typeof this.details === 'object' && 'issues' in this.details) {
          const issues = (this.details as any).issues;
          if (Array.isArray(issues) && issues.length > 0) {
            return issues[0].message;
          }
        }
        return this.message || 'Please check your input and try again.';
      case 'http':
        if (this.status >= 500) {
          return 'A server error occurred. Please try again later.';
        }
        return this.message || 'An error occurred. Please try again.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Convert to plain object for logging/serialization
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      status: this.status,
      code: this.code,
      details: this.details,
      isRetryable: this.isRetryable,
      stack: this.stack,
    };
  }
}

/**
 * Type guard for ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Helper to handle any error and convert to ApiError
 */
export async function toApiError(error: unknown): Promise<ApiError> {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof z.ZodError) {
    return ApiError.fromZodError(error);
  }

  return ApiError.fromKyError(error);
}
